import React, { useState, useRef, useEffect } from 'react';

export interface Option {
  value: string;
  label: string;
}

export interface MultiSelectFieldProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: readonly Option[];
  error?: string;
  placeholder?: string;
  required?: boolean;
  id?: string;
  disabled?: boolean;
  maxHeight?: string;
  isOptionDisabled?: (optionValue: string) => boolean;
}

/**
 * Reusable multi-select field component with searchable list
 * Stores array of selected option keys
 * No external libraries - pure React implementation
 */
export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  error,
  placeholder = 'Search and select options...',
  required = false,
  id,
  disabled = false,
  maxHeight = '200px',
  isOptionDisabled,
}) => {
  const fieldId = id || `multi-select-field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${fieldId}-error`;
  const dropdownId = `${fieldId}-dropdown`;
  const searchInputId = `${fieldId}-search`;
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedValues = Array.isArray(value) ? value : [];

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const toggleOption = (optionValue: string) => {
    if (disabled) return;
    if (isOptionDisabled && isOptionDisabled(optionValue)) return;

    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((v) => v !== optionValue));
    } else {
      onChange([...selectedValues, optionValue]);
    }
  };

  const removeOption = (optionValue: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    onChange(selectedValues.filter((v) => v !== optionValue));
  };

  const getSelectedLabels = () => {
    return selectedValues
      .map((val) => options.find((opt) => opt.value === val)?.label)
      .filter(Boolean) as string[];
  };

  const selectedLabels = getSelectedLabels();

  return (
    <div style={{ marginBottom: '15px', position: 'relative' }} ref={containerRef}>
      <label 
        htmlFor={searchInputId}
        style={{ 
          display: 'block', 
          marginBottom: '5px', 
          fontWeight: required ? 'bold' : 'normal' 
        }}
      >
        {label}
        {required && <span style={{ color: 'red', marginLeft: '4px' }} aria-label="required">*</span>}
      </label>
      
      {/* Selected values display / Input trigger */}
      <div
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={dropdownId}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? errorId : undefined}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          minHeight: '38px',
          padding: '4px 8px',
          fontSize: '14px',
          border: error ? '1px solid #dc3545' : '1px solid #ccc',
          borderRadius: '4px',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxSizing: 'border-box',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          alignItems: 'center',
        }}
      >
        {selectedLabels.length === 0 ? (
          <span style={{ color: '#999', fontSize: '14px' }}>{placeholder}</span>
        ) : (
          selectedLabels.map((label, index) => {
            const optionValue = selectedValues[index];
            return (
              <span
                key={optionValue}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 6px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '4px',
                  fontSize: '12px',
                  gap: '4px',
                }}
              >
                {label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => removeOption(optionValue, e)}
                    aria-label={`Remove ${label}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0',
                      fontSize: '14px',
                      lineHeight: '1',
                      color: '#666',
                      marginLeft: '4px',
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })
        )}
        <span style={{ marginLeft: 'auto', color: '#666' }}>▼</span>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          id={dropdownId}
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 1000,
            width: '100%',
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            maxHeight,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
            <input
              ref={searchInputRef}
              id={searchInputId}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search options..."
              style={{
                width: '100%',
                padding: '6px',
                fontSize: '14px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Options list */}
          <div
            style={{
              overflowY: 'auto',
              maxHeight: `calc(${maxHeight} - 50px)`,
            }}
          >
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '12px', color: '#999', textAlign: 'center' }}>
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                const optionDisabled = isOptionDisabled ? isOptionDisabled(option.value) : false;
                return (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={optionDisabled}
                    onClick={() => !optionDisabled && toggleOption(option.value)}
                    style={{
                      padding: '8px 12px',
                      cursor: optionDisabled ? 'not-allowed' : 'pointer',
                      backgroundColor: isSelected ? '#e7f3ff' : 'white',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: optionDisabled ? 0.5 : 1,
                      color: optionDisabled ? '#999' : 'inherit',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected && !optionDisabled) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected && !optionDisabled) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={optionDisabled}
                      onChange={() => {}} // Handled by parent onClick
                      style={{ margin: 0, cursor: optionDisabled ? 'not-allowed' : 'pointer' }}
                      tabIndex={-1}
                    />
                    <span>{option.label}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {error && (
        <div
          id={errorId}
          role="alert"
          style={{
            color: '#dc3545',
            fontSize: '12px',
            marginTop: '4px',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

